import { useMutation, useQuery } from '@tanstack/react-query';

import publicESignFetch from '@/adapters/publicESignFetch';

import {
  isTerminalLinkState,
  normalizePublicSignerError,
  type PublicSignerError
} from '../publicSignerError';
import type { PublicSignerSubmitRequest } from '../publicSignerTypes';
import {
  assertPublicSignerSessionResponse,
  assertPublicSignerSubmitResponse
} from '../publicSignerValidation';

export const PUBLIC_ESIGN_SIGNER_QUERY_KEY = 'public-esign-signer';

const MAX_SESSION_QUERY_RETRIES = 2;

export function usePublicESignSigner(token: string | undefined) {
  const sessionQuery = useQuery({
    queryKey: [PUBLIC_ESIGN_SIGNER_QUERY_KEY, token],
    queryFn: async () => {
      try {
        const response = await publicESignFetch.get<unknown>(
          `/public/signers/${encodeURIComponent(token ?? '')}`
        );

        return assertPublicSignerSessionResponse(response);
      } catch (error) {
        throw await normalizePublicSignerError(
          error,
          'Failed to load this signing session.'
        );
      }
    },
    enabled: Boolean(token),
    retry: (failureCount, error) => {
      if (isTerminalLinkState((error as PublicSignerError).linkState)) {
        return false;
      }
      return failureCount < MAX_SESSION_QUERY_RETRIES;
    }
  });

  const submitMutation = useMutation({
    mutationFn: async (payload: PublicSignerSubmitRequest) => {
      if (!token) {
        throw new Error('Missing signing token.');
      }
      try {
        const response = await publicESignFetch.post<unknown>(
          `/public/signers/${encodeURIComponent(token)}/submit`,
          payload
        );

        return assertPublicSignerSubmitResponse(response);
      } catch (error) {
        throw await normalizePublicSignerError(
          error,
          'Failed to complete this signing session.'
        );
      }
    }
  });

  return {
    sessionQuery,
    submitMutation
  };
}
